import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowLeft, Search, Library, Pencil, Dumbbell } from "lucide-react";
import { useProfile } from "@/hooks/useProfile";
import { useTeacherRoutinesPortfolio } from "@/hooks/useTeacherRoutinesPortfolio";
import { RoutineShimmer } from "@/components/routines/RoutineShimmer";

const PortafolioRutinas = () => {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { data: routines, isLoading } = useTeacherRoutinesPortfolio();
  const [search, setSearch] = useState("");

  const isAllowed =
    profile?.role === "profesor" ||
    profile?.role === "manager" ||
    profile?.role === "admin";

  const filtered = useMemo(() => {
    const list = routines || [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (r) =>
        r.name?.toLowerCase().includes(q) ||
        r.exercises.some((e) => e.name?.toLowerCase().includes(q))
    );
  }, [routines, search]);

  if (profile && !isAllowed) {
    return (
      <div className="min-h-screen bg-background text-primary">
        <main className="container mx-auto px-4 py-6">
          <p className="text-center text-primary/60">
            Esta sección está disponible solo para profesores.
          </p>
          <div className="text-center mt-4">
            <Button variant="outline" onClick={() => navigate("/rutina")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/10 via-background to-background text-primary">
      <main className="container pt-16 mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate("/rutina")}
              className="text-primary hover:text-primary/80"
            >
              <ArrowLeft className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
              Volver
            </Button>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold text-primary flex items-center gap-2">
              <Library className="h-6 w-6" />
              Portafolio de rutinas
            </h1>
          </div>
        </div>

        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar rutina o ejercicio..."
            className="pl-9"
          />
        </div>

        {isLoading ? (
          <RoutineShimmer />
        ) : !filtered.length ? (
          <div className="text-center py-16 text-primary/60">
            {routines && routines.length === 0
              ? "Todavía no creaste ninguna rutina."
              : "No se encontraron rutinas con ese término."}
          </div>
        ) : (
          <Accordion type="multiple" className="space-y-3">
            {filtered.map((r) => (
              <AccordionItem
                key={r.id}
                value={r.id}
                className="border border-primary/20 rounded-xl bg-primary/5 backdrop-blur-sm overflow-hidden"
              >
                <AccordionTrigger className="px-4 py-3 hover:no-underline">
                  <div className="flex items-center gap-3 w-full">
                    {r.image_url ? (
                      <img
                        src={r.image_url}
                        alt={r.name}
                        className="h-10 w-10 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Dumbbell className="h-5 w-5 text-primary/60" />
                      </div>
                    )}
                    <div className="flex-1 text-left">
                      <div className="font-semibold text-primary">{r.name}</div>
                      <div className="text-xs text-primary/60">
                        {r.exercises.length} ejercicio
                        {r.exercises.length === 1 ? "" : "s"} ·{" "}
                        {new Date(r.created_at).toLocaleDateString("es-AR")}
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="flex justify-end mb-3">
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/rutina/portafolio/${r.id}/editar`);
                      }}
                    >
                      <Pencil className="mr-2 h-4 w-4" />
                      Editar rutina
                    </Button>
                  </div>

                  {r.exercises.length === 0 ? (
                    <div className="text-sm text-primary/60 text-center py-4">
                      Esta rutina aún no tiene ejercicios.
                    </div>
                  ) : (
                    <div className="rounded-lg border border-primary/10 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-primary/5 text-primary/70">
                          <tr>
                            <th className="text-left px-3 py-2">Ejercicio</th>
                            <th className="text-center px-3 py-2">Series</th>
                            <th className="text-center px-3 py-2">Reps</th>
                            <th className="text-center px-3 py-2">Peso</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.exercises.map((ex) => (
                            <tr
                              key={ex.id}
                              className="border-t border-primary/10"
                            >
                              <td className="px-3 py-2 text-primary">
                                {ex.name}
                              </td>
                              <td className="px-3 py-2 text-center text-primary/80">
                                {ex.sets}
                              </td>
                              <td className="px-3 py-2 text-center text-primary/80">
                                {ex.reps}
                              </td>
                              <td className="px-3 py-2 text-center text-primary/80">
                                {ex.weight ? `${ex.weight} kg` : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </main>
    </div>
  );
};

export default PortafolioRutinas;
